import { useState, useEffect } from "react";
import { 
  subscribeInstallAvailability, 
  triggerInstall,
  isInstallAvailable 
} from "../utils/pwaInstallManager";

/**
 * Hook to access PWA install functionality
 * Uses global pwaInstallManager to reliably capture beforeinstallprompt event
 */
export function useInstallPrompt() {
  const [canInstall, setCanInstall] = useState(() => isInstallAvailable());
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      setCanInstall(false);
      return;
    }

    // Also check navigator.standalone for iOS Safari
    if (window.navigator.standalone === true) {
      setIsInstalled(true);
      setCanInstall(false);
      return;
    }

    // Subscribe to global install manager
    const unsubscribe = subscribeInstallAvailability((isAvailable) => {
      setCanInstall(isAvailable && !isInstalled);
    });

    // Listen for app installed event
    const installedHandler = () => {
      setIsInstalled(true);
      setCanInstall(false);
    };

    window.addEventListener("appinstalled", installedHandler);

    return () => {
      unsubscribe();
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, [isInstalled]);

  const promptInstall = async () => {
    const result = await triggerInstall();
    return result?.outcome === "accepted";
  };

  return {
    canInstall: canInstall && !isInstalled,
    isInstalled,
    promptInstall,
  };
}
